import { AUTH0_ELEPHANT_CLAIM_NAMESPACE } from "$global-providers/user-provider/provider";
import { useAuth0 } from "@auth0/auth0-react";
import { EpocusPlatformRole, EpocusRole } from "elephant-web/gql";
import { useMemo } from "react";

export type EpocusMembership = {
  programId: string;
  role: EpocusRole;
};

export type EpocusClaim = {
  userId: string | null;
  platformRole: EpocusPlatformRole | null;
  memberships: readonly EpocusMembership[];
};

const EPOCUS_ROLES = new Set<string>(Object.values(EpocusRole));
const EPOCUS_PLATFORM_ROLES = new Set<string>(
  Object.values(EpocusPlatformRole),
);

const asRole = (value: unknown): EpocusRole | null =>
  typeof value === "string" && EPOCUS_ROLES.has(value)
    ? (value as EpocusRole)
    : null;

const asPlatformRole = (value: unknown): EpocusPlatformRole | null =>
  typeof value === "string" && EPOCUS_PLATFORM_ROLES.has(value)
    ? (value as EpocusPlatformRole)
    : null;

// Rejecting the whole list rather than skipping a malformed entry: a partial
// parse would silently narrow the caller's programs, which presents as losing
// access to some of your own work rather than as a broken claim.
const asMemberships = (value: unknown): readonly EpocusMembership[] | null => {
  if (!Array.isArray(value)) return null;

  const parsed: EpocusMembership[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) return null;
    const { programId, role } = entry as Record<string, unknown>;
    const epocusRole = asRole(role);
    if (typeof programId !== "string" || epocusRole === null) return null;
    parsed.push({ programId, role: epocusRole });
  }

  return parsed;
};

export const useEpocusClaim = (): EpocusClaim | null => {
  const { user: auth0User } = useAuth0();
  const raw = auth0User?.[AUTH0_ELEPHANT_CLAIM_NAMESPACE]?.epocus;

  return useMemo(() => {
    if (typeof raw !== "object" || raw === null) return null;

    const { userId, platformRole, memberships } = raw as Record<
      string,
      unknown
    >;
    const parsed = asMemberships(memberships);
    if (parsed === null) return null;

    return {
      userId: typeof userId === "string" ? userId : null,
      platformRole: asPlatformRole(platformRole),
      memberships: parsed,
    };
  }, [raw]);
};

/**
 * Null rather than the highest-precedence role for a caller in several
 * programs. Collapsing them to a maximum renders Program Admin navigation in a
 * program the caller only performs in, while every per-program server guard
 * correctly refuses them. `selectActiveProgramId` resolves to null on the same
 * condition, and this agrees with it.
 */
export const useEpocusRole = (): EpocusRole | null => {
  const claim = useEpocusClaim();
  const memberships = claim?.memberships ?? [];

  return memberships.length === 1 ? (memberships[0]?.role ?? null) : null;
};

export const useIsEpocusPlatformAdmin = (): boolean =>
  useEpocusClaim()?.platformRole === EpocusPlatformRole.EpocusAdmin;
